import { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import {
  Box,
  Typography,
  Paper,
  Stack,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  TextField,
  Alert,
} from '@mui/material'
import SaveIcon from '@mui/icons-material/Save'
import CancelIcon from '@mui/icons-material/Cancel'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import SettingsIcon from '@mui/icons-material/Settings'
import ViewModuleIcon from '@mui/icons-material/ViewModule'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import GridLayout, { Layout } from 'react-grid-layout'
import 'react-grid-layout/css/styles.css'
import 'react-resizable/css/styles.css'
import { WidgetConfig, WidgetType } from '@/components/widgets/types'
import {
  KPIScorecardWidget,
  ActivityFeedWidget,
  AlertCenterWidget,
} from '@/components/widgets'
import { kpiService } from '@/services/kpiService'
import { KPIMetric } from '@/components/widgets/types'

interface DashboardBuilderProps {
  onSave?: (config: WidgetConfig[]) => void
  onCancel?: () => void
  initialConfig?: WidgetConfig[]
}

const WIDGET_TYPES: { type: WidgetType; label: string; icon: React.ReactNode; description: string }[] = [
  {
    type: 'kpi-scorecard',
    label: 'KPI Scorecard',
    icon: <ViewModuleIcon />,
    description: 'Display key performance indicators with targets',
  },
  {
    type: 'activity-feed',
    label: 'Activity Feed',
    icon: <ViewModuleIcon />,
    description: 'Real-time transaction activity stream',
  },
  {
    type: 'alert-center',
    label: 'Alert Center',
    icon: <ViewModuleIcon />,
    description: 'System alerts and notifications',
  },
]

const DashboardBuilder = ({ onSave, onCancel, initialConfig = [] }: DashboardBuilderProps) => {
  const [widgets, setWidgets] = useState<WidgetConfig[]>(initialConfig)
  const [editingWidget, setEditingWidget] = useState<WidgetConfig | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [builderMode, setBuilderMode] = useState(true)
  const [kpiMetrics, setKpiMetrics] = useState<KPIMetric[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [containerWidth, setContainerWidth] = useState(1200)
  const containerRef = useRef<HTMLDivElement>(null)

  // Sync widgets when initialConfig changes
  useEffect(() => {
    if (initialConfig.length > 0) {
      setWidgets(initialConfig)
    }
  }, [initialConfig])

  // Update container width on resize
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth - 64 // Account for padding and margins
        setContainerWidth(Math.max(width, 800)) // Minimum width of 800px
      } else {
        // Fallback if ref not ready
        const width = window.innerWidth - 300 // Approximate sidebar + padding
        setContainerWidth(Math.max(width, 800))
      }
    }

    // Initial update
    updateWidth()
    
    // Update on resize with debounce
    let timeoutId: NodeJS.Timeout
    const handleResize = () => {
      clearTimeout(timeoutId)
      timeoutId = setTimeout(updateWidth, 100)
    }

    window.addEventListener('resize', handleResize)
    // Also update after a short delay to ensure DOM is ready
    const initTimeout = setTimeout(updateWidth, 100)
    
    return () => {
      window.removeEventListener('resize', handleResize)
      clearTimeout(timeoutId)
      clearTimeout(initTimeout)
    }
  }, [])

  // Load KPI metrics with better error handling
  useEffect(() => {
    let mounted = true
    setLoading(true)
    setError(null)
    
    kpiService
      .getKPIMetrics(24)
      .then((metrics) => {
        if (mounted) {
          setKpiMetrics(metrics)
          setLoading(false)
        }
      })
      .catch((err) => {
        if (mounted) {
          console.warn('Failed to load KPI metrics:', err)
          // Don't show error to user, just use empty array
          setKpiMetrics([])
          setLoading(false)
          // Only set error if it's not a 500 error (which might be a backend issue)
          if (err?.response?.status === 500) {
            // Silently handle 500 errors - they're backend issues
            console.error('Backend error loading KPI metrics:', err)
          } else if (err?.response?.status) {
            setError('Failed to load some dashboard data. Widgets may not display correctly.')
          }
        }
      })

    return () => {
      mounted = false
    }
  }, [])

  // Convert WidgetConfig to GridLayout format
  const layout = useMemo<Layout[]>(() => {
    return widgets
      .filter((w) => w.visible)
      .map((widget) => ({
        i: widget.id,
        x: Math.max(0, Math.min(widget.position.x, 11)), // Clamp x to 0-11
        y: widget.position.y,
        w: Math.max(1, Math.min(widget.size.width, 12)), // Clamp width to 1-12
        h: Math.max(1, Math.min(widget.size.height, 20)), // Clamp height to 1-20
        minW: 1,
        minH: 1,
        maxW: 12,
        maxH: 20,
      }))
  }, [widgets])

  // Handle layout change (drag and resize)
  const handleLayoutChange = useCallback((newLayout: Layout[]) => {
    setWidgets((prevWidgets) => {
      return prevWidgets.map((widget) => {
        const layoutItem = newLayout.find((item) => item.i === widget.id)
        if (layoutItem) {
          return {
            ...widget,
            position: { x: layoutItem.x, y: layoutItem.y },
            size: { width: layoutItem.w, height: layoutItem.h },
          }
        }
        return widget
      })
    })
  }, [])

  const addWidget = (type: WidgetType) => {
    // Find the next available position
    const maxY = Math.max(...widgets.map((w) => w.position.y + w.size.height), 0)
    
    const newWidget: WidgetConfig = {
      id: `widget-${Date.now()}`,
      type,
      title: WIDGET_TYPES.find((w) => w.type === type)?.label || type,
      position: { x: 0, y: maxY },
      size: { width: 4, height: 3 },
      refreshInterval: 60,
      filters: [],
      settings: {},
      visible: true,
    }
    setWidgets([...widgets, newWidget])
    setDrawerOpen(false)
  }

  const removeWidget = (id: string) => {
    setWidgets(widgets.filter((w) => w.id !== id))
  }

  const updateWidget = (id: string, updates: Partial<WidgetConfig>) => {
    setWidgets(widgets.map((w) => (w.id === id ? { ...w, ...updates } : w)))
  }

  const handleSave = () => {
    if (onSave) {
      onSave(widgets)
    }
    // Save to localStorage as well
    localStorage.setItem('dashboard-config', JSON.stringify(widgets))
    setBuilderMode(false)
  }

  const handleCancel = () => {
    if (onCancel) {
      onCancel()
    }
    setBuilderMode(false)
  }

  const renderWidget = (config: WidgetConfig) => {
    const commonProps = {
      config,
      onUpdate: (updates: Partial<WidgetConfig>) => updateWidget(config.id, updates),
      onRemove: builderMode ? () => removeWidget(config.id) : undefined,
    }

    switch (config.type) {
      case 'kpi-scorecard':
        const metric = kpiMetrics.find((m) => m.id === config.settings?.metricId) || kpiMetrics[0]
        if (!metric && kpiMetrics.length === 0) {
          return (
            <Paper sx={{ p: 2, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px dashed #ccc' }}>
              <Typography variant="body2" color="text.secondary">
                {config.title} - Loading...
              </Typography>
            </Paper>
          )
        }
        if (!metric) {
          return (
            <Paper sx={{ p: 2, height: '100%', border: '2px dashed #ccc' }}>
              <Typography variant="h6" gutterBottom>{config.title}</Typography>
              <Typography variant="body2" color="text.secondary">No metric data available</Typography>
            </Paper>
          )
        }
        return <KPIScorecardWidget {...commonProps} metric={metric} />
      case 'activity-feed':
        return (
          <ActivityFeedWidget
            {...commonProps}
            maxItems={config.settings?.maxItems || 50}
            autoScroll={config.settings?.autoScroll !== false}
            soundAlerts={config.settings?.soundAlerts === true}
            alertThreshold={config.settings?.alertThreshold || 10000}
          />
        )
      case 'alert-center':
        return <AlertCenterWidget {...commonProps} maxAlerts={config.settings?.maxAlerts || 10} />
      case 'custom-chart':
      case 'data-table':
      case 'system-health':
      case 'anomaly-detection':
      case 'heat-map':
      case 'processing-queue':
      case 'revenue-trend':
      default:
        return (
          <Paper 
            sx={{ 
              p: 2, 
              height: '100%', 
              display: 'flex',
              flexDirection: 'column',
              border: builderMode ? '2px dashed #1976d2' : '1px solid #e0e0e0',
              bgcolor: builderMode ? 'rgba(25, 118, 210, 0.05)' : 'background.paper',
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
              {config.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Type: {config.type}
            </Typography>
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px dashed #ccc', borderRadius: 1, p: 2 }}>
              <Typography variant="body2" color="text.secondary" align="center">
                {builderMode 
                  ? 'This widget will be rendered in the dashboard view'
                  : 'Widget content will be displayed here'}
              </Typography>
            </Box>
            {builderMode && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                Size: {config.size.width} × {config.size.height} | Position: ({config.position.x}, {config.position.y})
              </Typography>
            )}
          </Paper>
        )
    }
  }

  return (
    <Box>
      {error && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {builderMode && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Dashboard Builder Mode</Typography>
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setDrawerOpen(true)}
              >
                Add Widget
              </Button>
              <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave}>
                Save Dashboard
              </Button>
              <Button variant="outlined" startIcon={<CancelIcon />} onClick={handleCancel}>
                Cancel
              </Button>
            </Stack>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Drag widgets to move them, drag corners to resize
          </Typography>
        </Paper>
      )}

      <Box 
        ref={containerRef}
        sx={{ 
          position: 'relative', 
          minHeight: '600px',
          width: '100%',
          '& .react-grid-layout': {
            position: 'relative',
          },
          '& .react-grid-item': {
            transition: 'all 200ms ease',
            transitionProperty: 'left, top, width, height',
          },
          '& .react-grid-item.cssTransforms': {
            transitionProperty: 'transform, width, height',
          },
          '& .react-grid-item.resizing': {
            transition: 'none',
            zIndex: 1,
            willChange: 'width, height',
          },
          '& .react-grid-item.react-draggable-dragging': {
            transition: 'none',
            zIndex: 3,
            willChange: 'transform',
          },
          '& .react-grid-item.dropping': {
            visibility: 'hidden',
          },
          '& .react-grid-item.react-grid-placeholder': {
            background: 'rgba(25, 118, 210, 0.2)',
            opacity: 0.2,
            transitionDuration: '100ms',
            zIndex: 2,
            userSelect: 'none',
            border: '2px dashed #1976d2',
            borderRadius: 1,
          },
          '& .react-resizable-handle': {
            position: 'absolute',
            width: '20px',
            height: '20px',
            bottom: 0,
            right: 0,
            background: 'url("data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNiIgaGVpZ2h0PSI2IiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxnIGZpbGw9IiM5OTkiIGZpbGwtcnVsZT0iZXZlbm9kZCI+PHBhdGggZD0ibTEzIDExIDIgMi0yIDItMi0yIDItMnoiLz48L2c+PC9zdmc+")',
            backgroundPosition: 'bottom right',
            padding: '0 3px 3px 0',
            backgroundRepeat: 'no-repeat',
            backgroundOrigin: 'content-box',
            boxSizing: 'border-box',
            cursor: 'se-resize',
          },
        }}
      >
        {containerWidth > 0 && (
          <GridLayout
            className="layout"
            layout={layout}
            cols={12}
            rowHeight={60}
            width={containerWidth}
            onLayoutChange={handleLayoutChange}
            isDraggable={builderMode}
            isResizable={builderMode}
            draggableHandle=".drag-handle"
            compactType={null}
            preventCollision={false}
            margin={[16, 16]}
            useCSSTransforms={true}
          >
          {widgets
            .filter((w) => w.visible)
            .map((widget) => (
              <Box
                key={widget.id}
                sx={{
                  position: 'relative',
                  height: '100%',
                  width: '100%',
                }}
              >
                {builderMode && (
                  <>
                    {/* Drag Handle - separate from buttons */}
                    <Box
                      className="drag-handle"
                      sx={{
                        position: 'absolute',
                        top: 8,
                        left: 8,
                        zIndex: 9,
                        width: '24px',
                        height: '24px',
                        bgcolor: 'primary.main',
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: 2,
                        cursor: 'move',
                        '&:hover': {
                          bgcolor: 'primary.dark',
                        },
                      }}
                      title="Drag to move"
                    >
                      <DragIndicatorIcon sx={{ color: 'white', fontSize: 16 }} />
                    </Box>
                    {/* Action Buttons - separate from drag handle */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        zIndex: 11,
                        display: 'flex',
                        gap: 0.5,
                        bgcolor: 'background.paper',
                        borderRadius: 1,
                        p: 0.5,
                        boxShadow: 2,
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation()
                          e.preventDefault()
                          setEditingWidget(widget)
                        }}
                        sx={{ 
                          bgcolor: 'action.hover',
                          '&:hover': { bgcolor: 'action.selected' }
                        }}
                        title="Edit widget"
                      >
                        <SettingsIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation()
                          e.preventDefault()
                          if (window.confirm(`Are you sure you want to delete "${widget.title}"?`)) {
                            removeWidget(widget.id)
                          }
                        }}
                        sx={{ 
                          bgcolor: 'error.light', 
                          color: 'error.contrastText',
                          '&:hover': { bgcolor: 'error.main' }
                        }}
                        title="Delete widget"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </>
                )}
                <Box 
                  sx={{ 
                    height: '100%', 
                    width: '100%', 
                    overflow: 'auto',
                    '& > *': {
                      height: '100%',
                    }
                  }}
                >
                  {renderWidget(widget)}
                </Box>
              </Box>
            ))}
          </GridLayout>
        )}
        {containerWidth === 0 && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Loading dashboard...
            </Typography>
          </Box>
        )}
      </Box>

      {/* Widget Library Drawer */}
      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: 350, p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Widget Library
          </Typography>
          <Divider sx={{ my: 2 }} />
          <List>
            {WIDGET_TYPES.map((widgetType) => (
              <ListItem key={widgetType.type} disablePadding>
                <ListItemButton onClick={() => addWidget(widgetType.type)}>
                  <ListItemIcon>{widgetType.icon}</ListItemIcon>
                  <ListItemText
                    primary={widgetType.label}
                    secondary={widgetType.description}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>

      {/* Widget Configuration Dialog */}
      <Dialog
        open={!!editingWidget}
        onClose={() => setEditingWidget(null)}
        maxWidth="sm"
        fullWidth
      >
        {editingWidget && (
          <>
            <DialogTitle>Configure Widget</DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ mt: 1 }}>
                <TextField
                  label="Widget Title"
                  value={editingWidget.title}
                  onChange={(e) =>
                    updateWidget(editingWidget.id, { title: e.target.value })
                  }
                  fullWidth
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={editingWidget.visible}
                      onChange={(e) =>
                        updateWidget(editingWidget.id, { visible: e.target.checked })
                      }
                    />
                  }
                  label="Visible"
                />
                <Typography variant="subtitle2" sx={{ mt: 2 }}>
                  Size: {editingWidget.size.width} × {editingWidget.size.height} grid units
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Position: X: {editingWidget.position.x}, Y: {editingWidget.position.y}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  You can drag and resize widgets directly on the dashboard
                </Typography>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setEditingWidget(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  )
}

export default DashboardBuilder
